import React, { useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { Helmet } from 'react-helmet-async';
import Header from './Header';
import Footer from './Footer';
import { useJobs, useCategories } from '../hooks/useApi';
import { getCategoryMeta } from '../config/categories';
import { Link } from 'react-router-dom';

const CategoryJobs = () => {
  const { category } = useParams();
  const navigate = useNavigate();
  useEffect(() => {
    window.scrollTo(0, 0); // Scroll to top when this component mounts
  }, []);
  // Use React Query hooks
  const { data: jobsData, isLoading: jobsLoading, error: jobsError } = useJobs();
  const { data: categoriesData, isLoading: categoriesLoading } = useCategories();
  
  // Combine loading states
  const isLoading = jobsLoading || categoriesLoading;
  
  // Find the category ID that matches the URL slug
  const categoryId = categoriesData ? Object.entries(categoriesData).find(
    ([id, name]) => name.toLowerCase().replace(/\s+/g, '-') === category
  )?.[0] : null;
  
  // Filter jobs by category
  const jobs = jobsData && categoryId ? jobsData
    .filter(job => job.category.toString() === categoryId)
    .map(job => ({
      id: job.job_id,
      jobTitle: job.title || '',
      jobDescription: job.description || '',
      jobPosition: job.position || '',
      skills: [],
      jobType: job.position || 'Full Time',
      package: job.package ? (job.package ).toString() : '0',
      location: job.location || 'Not Specified',
      category: job.category ? job.category.toString() : '',
      companyDetails: job.company ? job.company.toString() : '',
      image: '',
      job_code: job.job_code || '',
      location_name: job.location_name || '',
      company_name: job.company_name || '',
      company_image_url: job.company_image_url || ''
    })) : [];

  const handleJobClick = (jobId, jobTitle, jobCode) => {
    // If job code is available, use the short URL format
    if (jobCode) {
      navigate(`/jobs/${jobCode}`);
      return;
    }
    
    // If job title is available, create a URL-friendly slug
    if (jobTitle) {
      const slugifiedTitle = jobTitle
        .toLowerCase()
        .replace(/\s+/g, '-')
        .replace(/[^\w\-]+/g, '')
        .replace(/\-\-+/g, '-')
        .replace(/^-+/, '')
        .replace(/-+$/, '');
      
      if (slugifiedTitle) {
        navigate(`/jobs/${slugifiedTitle}`);
        return;
      }
    }
    
    // Fallback to the original URL format
    const formattedTitle = jobTitle ? jobTitle.toLowerCase().replace(/\s+/g, '+') : 'job';
    navigate(`/job-detail/${formattedTitle}/${jobId}`);
  };

  // Get the display name of the category
  const categoryDisplayName = categoriesData ? Object.entries(categoriesData)
    .find(([id, name]) => name.toLowerCase().replace(/\s+/g, '-') === category)?.[1] || category : category;
    
  // Get meta information for this category
  const categoryMeta = getCategoryMeta(category);
  
  // Generate meta description
  const generateMetaDescription = () => {
    const jobCount = jobs.length;
    
    if (jobCount === 0) {
      return `No ${categoryDisplayName} jobs available at the moment. Check back later for new opportunities.`;
    }
    
    return `Browse ${jobCount} ${categoryDisplayName} job opportunities. Find the best ${categoryDisplayName} positions with salary information, company details, and job requirements.`;
  };
  
  // Generate canonical URL
  const generateCanonicalUrl = () => {
    const baseUrl = window.location.origin;
    return `${baseUrl}/category/${category}`;
  };

  // Set meta tags directly in the document head
  useEffect(() => {
    if (!categoriesLoading && categoryDisplayName) {
      // Function to set meta tags
      const setMetaTag = (name, content) => {
        if (!content) return;
        
        // Handle both property and name meta tags
        let metaTag;
        if (name.startsWith('og:') || name.startsWith('twitter:')) {
          metaTag = document.querySelector(`meta[property="${name}"]`);
          if (!metaTag) {
            metaTag = document.createElement('meta');
            metaTag.setAttribute('property', name);
            metaTag.setAttribute('data-dynamic', 'true');
            document.head.appendChild(metaTag);
          }
        } else {
          metaTag = document.querySelector(`meta[name="${name}"]`);
          if (!metaTag) {
            metaTag = document.createElement('meta');
            metaTag.setAttribute('name', name);
            metaTag.setAttribute('data-dynamic', 'true');
            document.head.appendChild(metaTag);
          }
        }
        
        metaTag.setAttribute('content', content);
      };
      
      // Generate description based on current data
      const description = generateMetaDescription();
      const title = `${categoryDisplayName} Jobs - Fresher Job Adda`;
      
      // Set title
      document.title = title;
      
      // Set meta tags
      setMetaTag('description', description);
      setMetaTag('keywords', categoryMeta.keywords || `jobs, career, ${categoryDisplayName.toLowerCase()}, hiring, employment, job listing`);
      
      // Set Open Graph tags
      setMetaTag('og:title', title);
      setMetaTag('og:description', description);
      setMetaTag('og:url', generateCanonicalUrl());
      setMetaTag('og:type', 'website');
      setMetaTag('og:image', '/logo192.png');
      
      // Set Twitter Card tags
      setMetaTag('twitter:card', 'summary_large_image');
      setMetaTag('twitter:title', title);
      setMetaTag('twitter:description', description);
      setMetaTag('twitter:image', '/logo192.png');
      
      // Set canonical link
      const canonicalUrl = generateCanonicalUrl();
      let canonicalLink = document.querySelector('link[rel="canonical"]');
      if (!canonicalLink) {
        canonicalLink = document.createElement('link');
        canonicalLink.setAttribute('rel', 'canonical');
        canonicalLink.setAttribute('data-dynamic', 'true');
        document.head.appendChild(canonicalLink);
      }
      canonicalLink.setAttribute('href', canonicalUrl);
      
      // Cleanup function to remove dynamic meta tags when component unmounts
      return () => {
        document.querySelectorAll('meta[data-dynamic="true"]').forEach(el => el.remove());
        document.querySelectorAll('link[data-dynamic="true"]').forEach(el => el.remove());
      };
    }
  }, [category, categoryDisplayName, jobs.length, categoriesLoading]);

  return (
    <>
      <Header />
      <div className="page-title">
        <div className="container">
          <div className="page-caption">
            <h2>{categoryDisplayName} Jobs</h2>
            <p>
              <Link to="/">Home</Link> <i className="fa fa-angle-double-right"></i> 
            
              <Link to="/categories">Categories</Link> <i className="fa fa-angle-double-right"></i>{categoryDisplayName}

            </p>
          </div>
        </div>
      </div>

      <section className="padd-top-80 padd-bot-80">
        <div className="container">
        <div className="row mb-4">
            <div className="col-12">
              <h2 className="mb-0">Open Positions</h2>
              <p className="text-muted">
                {jobs.length} {jobs.length === 1 ? 'job' : 'jobs'} available at {categoryDisplayName}
              </p>
            </div>
          </div>
          <div className="row">
            {isLoading ? (
              <div className="col-12 text-center">Loading jobs...</div>
            ) : jobsError ? (
              <div className="col-12 text-center">Error loading jobs: {jobsError.message}</div>
            ) : jobs.length === 0 ? (
              <div className="col-12 text-center">No jobs available in this category</div>
            ) : (
              jobs.map((job) => (
                <div className="col-md-3 col-sm-6" key={job.id}>
                  <div
                    className="utf_grid_job_widget_area pt-0"
                    onClick={() => handleJobClick(job.id, job.jobTitle, job.job_code)}
                    style={{ cursor: "pointer" }}
                  >
                    <div className="u-content px-0" style={{ position: "relative" }}>
                      <div className="avatar-square box-80" style={{ width: "100%", position: "relative" }}>
                        <a onClick={(e) => {
                          e.preventDefault();
                          e.stopPropagation();
                          handleJobClick(job.id, job.jobTitle, job.job_code);
                        }}>
                          <div 
                            className="category-icon-fallback"
                            style={{
                              display: 'flex',
                              width: "100%",
                              height: "200px",
                              backgroundColor: "#f8f9fa",
                              justifyContent: "center",
                              alignItems: "center",
                              fontSize: "4rem",
                              color: "#26ae61",
                              borderRadius: "8px",
                              padding: "2rem",
                              position: "relative",
                              zIndex: 1
                            }}
                          >
                            {/* Fallback icon - always present but hidden by default */}
                            <div 
                              className="fallback-icon"
                              style={{
                                position: "absolute",
                                top: 0,
                                left: 0,
                                right: 0,
                                bottom: 0,
                                display: "flex",
                                justifyContent: "center",
                                alignItems: "center",
                                backgroundColor: "#f8f9fa",
                                zIndex: 1
                              }}
                            >
                              <i
                                className="fa-solid fa-house"
                                style={{
                                  fontSize: "3.5rem",
                                  color: "#ccc"
                                }}
                              ></i>
                            </div>

                            {/* Company image */}
                            {job.company_image_url?.trim() ? (
                              <img
                                src={job.company_image_url}
                                alt={job.company_name}
                                loading="lazy"
                                onError={(e) => {
                                  e.target.style.display = "none";
                                  const fallbackIcon = e.target.parentNode.querySelector(".fallback-icon");
                                  if (fallbackIcon) {
                                    fallbackIcon.style.display = "flex";
                                  }
                                }}
                                style={{
                                  width: "100%",
                                  height: "100%",
                                  objectFit: "cover",
                                  position: "absolute",
                                  top: 0,
                                  left: 0,
                                  zIndex: 2
                                }}
                                className="company-logo"
                              />
                            ) : null}
                          </div>
                          
                        
                        </a>
                      </div>
                      <div style={{ position: "absolute", top: 0, left: 0, right: 0, zIndex: 3 }}>
                        <span className="job-type bg-success border-1 text-white position-absolute top-2 start-2">
                          {categoriesData && categoriesData[job.category] ? categoriesData[job.category] : job.category}
                        </span>
                      </div>
                      <h5 className="pt-3">
                        <a onClick={(e) => {
                          e.preventDefault();
                          e.stopPropagation();
                          handleJobClick(job.id, job.jobTitle, job.job_code);
                        }}>
                          {job.jobTitle}
                        </a>
                      </h5>
                      <p className="text-muted mb-0">
                        <i className="fa fa-briefcase"></i> {job.jobPosition}
                      </p>
                      <p className="text-muted mb-0">
                        <i className="fa fa-industry text-capitalize"></i> {job.company_name}
                      </p>
                      <p className="text-muted py-0 my-0">
                        <i className="fas fa-wallet"></i> {job.package}+ LPA
                      </p>
                      <p className="text-muted py-0 my-0 text-capitalize">
                          <i className="fas fa-map-marker-alt"></i> {job.location_name}
                        </p>
                        <p className="text-muted py-0 my-0 text-capitalize">
  <i className="fas fa-code
"></i> {job.job_code}
</p>
                    </div>
                    <div className="utf_apply_job_btn_item">
                      <a
                        onClick={(e) => {
                          e.preventDefault();
                          e.stopPropagation();
                          handleJobClick(job.id, job.jobTitle, job.job_code);
                        }}
                        className="btn job-browse-btn btn-radius br-light"
                      >
                        Apply
                      </a>
                    </div>
                  </div>
                </div>
              ))
            )}
          </div>
        </div>
      </section>
      <Footer />
    </>
  );
};

export default CategoryJobs; 